import React, { useState, useEffect, useMemo } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

const TOUR_STORAGE_KEY = 'hasSeenTour';

const ProductTour = () => {
  const [run, setRun] = useState(false);
  const location = useLocation();
  const { venderData } = useSelector((state) => state.vender);
  const vendorId = venderData?.id;

  // Determine if this vendor has already seen the tour
  const hasSeenTour = useMemo(() => {
    if (!vendorId) return true; // don't start until we know the user
    // Migration: if old browser-wide key exists, don't show tour again
    if (localStorage.getItem(TOUR_STORAGE_KEY)) return true;
    const stored = localStorage.getItem(`${TOUR_STORAGE_KEY}_${vendorId}`);
    return !!stored;
  }, [vendorId]);

  // Start tour when: on home page, vendor data loaded, hasn't seen tour, not mobile
  useEffect(() => {
    if (hasSeenTour) return;
    if (location.pathname !== '/') return;
    if (typeof window !== 'undefined' && window.innerWidth < 768) return;
    if (!vendorId) return;

    // Mark as seen immediately so refresh during tour doesn't replay
    localStorage.setItem(`${TOUR_STORAGE_KEY}_${vendorId}`, 'true');
    const timer = setTimeout(() => setRun(true), 1200);
    return () => clearTimeout(timer);
  }, [location.pathname, vendorId, hasSeenTour]);

  const steps = useMemo(() => [
    {
      target: 'body',
      placement: 'center',
      title: 'Welcome to Sportona!',
      content: "Let's take a quick tour of your coaching dashboard. This will help you understand how each feature works and how it helps you run your academy.",
      disableBeacon: true,
    },
    {
      target: '#tour-quick-add',
      placement: 'bottom',
      title: 'Quick Add — Create Services, Batches & Trainers',
      content: "You can easily create and launch new Services right here! Click 'Quick Add' anytime to add new sports services, schedule batches, or onboard trainers in just a few clicks.",
    },
    {
      target: '#tour-services',
      placement: 'right',
      title: 'Services — Your Revenue Streams',
      content: "Manage your training sessions like 'Under-12 Football' or 'Morning Fitness'. Each service defines the sport, schedule, price, and capacity. You can also create services directly from the Quick Add button above!",
    },
    {
      target: '#tour-staff',
      placement: 'right',
      title: 'Staff — Your Coaching Team',
      content: "Add your coaches and trainers here. Assign them to services so parents know who's leading each session. Track their schedules, availability, and performance — all in one place.",
    },
    {
      target: '#tour-finance',
      placement: 'right',
      title: 'Finances — Track Every Pound',
      content: "See your revenue, pending payouts, and subscription status at a glance. Connect Stripe to accept card payments directly. All transactions are tracked automatically — no manual bookkeeping.",
    },
    {
      target: '#tour-operations',
      placement: 'right',
      title: 'Operations — Day-to-Day Config',
      content: "Manage your training halls, set business hours, configure cancellation policies, and update your business profile. This is your academy's control center.",
    },
    {
      target: '#tour-trends',
      placement: 'top',
      title: 'Trends — Data-Driven Decisions',
      content: "Track subscriber growth, revenue trends, and attendance patterns in real time. AI-generated insights help you make smarter decisions about pricing and scheduling.",
    },
    {
      target: '#tour-setup',
      placement: 'right',
      title: 'Quick Start — Set Up in 60 Seconds',
      content: "New here? Go to Setup for a guided wizard that creates your service, batch, coach, and venue in one click. It auto-fills from Google — just enter your academy name!",
    },
  ], []);

  const handleJoyrideCallback = (data) => {
    const { status, action } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status) || action === 'close') {
      setRun(false);
      if (vendorId) {
        localStorage.setItem(`${TOUR_STORAGE_KEY}_${vendorId}`, 'true');
      }
    }
  };

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton
      run={run}
      scrollToFirstStep
      showProgress
      showSkipButton
      steps={steps}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: '#000',
          textColor: '#333',
        },
        buttonNext: {
          backgroundColor: '#000',
        },
        buttonBack: {
          marginRight: 10,
          color: '#666',
        },
      }}
    />
  );
};

export default ProductTour;
